import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Activity, 
  ShieldAlert, 
  Clock, 
  Zap, 
  ArrowUpRight, 
  Search,
  Filter,
  RefreshCw,
  MoreVertical,
  ChevronRight,
  TrendingUp,
  Scale
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { SLACountdown } from "@/components/dashboard/SLACountdown";
import { useApiClient } from "@/hooks/use-api-client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface Case {
  id: string;
  agent: string;
  sector: string;
  risk: string;
  timestamp: string;
  deadline: string;
  status: "active" | "completed";
}

export default function ControlCenter() {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const apiClient = useApiClient();

  const fetchData = async () => {
    try {
      setLoading(true);
      const [casesRes, statsRes] = await Promise.all([
        (apiClient as any).get("/api/v1/dashboard/active-cases"),
        (apiClient as any).get("/api/v1/dashboard/roi-summary")
      ]);
      setCases(casesRes || []);
      setStats(statsRes || null);
    } catch (error) {
      console.error("Dashboard error:", error);
      toast.error("Error al sincronizar datos en tiempo real");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  const getRiskColor = (risk: string) => {
    switch(risk) {
      case 'Crítico': return 'bg-red-600 text-white border-red-700';
      case 'Alto': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Medio': return 'bg-blue-50 text-blue-700 border-blue-100';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="p-8 space-y-8 bg-[#fdfdfd] min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <Zap className="h-8 w-8 text-blue-600 fill-blue-600/10" />
            Legal OS <span className="text-blue-600">Control Center</span>
          </h1>
          <p className="text-slate-500 font-medium mt-1">Supervisión táctica de ingesta legal y cumplimiento en tiempo real.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} className="rounded-xl gap-2 font-bold border-slate-200 shadow-sm">
            <RefreshCw className={loading ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5 text-blue-600"} />
            Sincronizar
          </Button>
          <Button className="rounded-xl bg-slate-900 text-white hover:bg-slate-800 shadow-xl shadow-slate-200 gap-2 font-bold">
            <Activity className="h-3.5 w-3.5" /> Live Monitor
          </Button>
        </div>
      </div>

      {/* Hero Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="rounded-[2rem] border-sky-100 bg-sky-50/30 shadow-sm overflow-hidden relative group">
          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform">
            <Scale className="h-16 w-16 text-sky-600" />
          </div>
          <CardHeader className="pb-2">
            <CardDescription className="text-sky-600 font-black uppercase text-[10px] tracking-widest">Ahorro Estimado</CardDescription>
            <CardTitle className="text-3xl font-black text-sky-900">{stats?.total_hours_saved || 0} hrs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-1 text-[10px] font-bold text-sky-600 bg-white/60 w-fit px-2 py-0.5 rounded-full border border-sky-100">
              <TrendingUp className="h-3 w-3" /> +12% vs mes anterior
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[2rem] border-rose-100 bg-rose-50/30 shadow-sm overflow-hidden relative group">
          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform">
            <ShieldAlert className="h-16 w-16 text-rose-600" />
          </div>
          <CardHeader className="pb-2">
            <CardDescription className="text-rose-600 font-black uppercase text-[10px] tracking-widest">Riesgo Crítico</CardDescription>
            <CardTitle className="text-3xl font-black text-rose-900">{stats?.risk_dist?.Crítico || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-1 text-[10px] font-bold text-rose-600 bg-white/60 w-fit px-2 py-0.5 rounded-full border border-rose-100">
              <Clock className="h-3 w-3" /> Atención Inmediata
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[2rem] border-slate-200 bg-white shadow-sm overflow-hidden relative group">
          <CardHeader className="pb-2">
            <CardDescription className="text-slate-400 font-black uppercase text-[10px] tracking-widest">Total Casos</CardDescription>
            <CardTitle className="text-3xl font-black text-slate-900">{stats?.total_cases || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-50 w-fit px-2 py-0.5 rounded-full border border-slate-100">
              <Activity className="h-3 w-3" /> Operación Constante
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[2rem] border-blue-100 bg-blue-600 shadow-xl shadow-blue-200 overflow-hidden relative group">
          <CardHeader className="pb-2">
            <CardDescription className="text-blue-100 font-black uppercase text-[10px] tracking-widest">Salud Operativa</CardDescription>
            <CardTitle className="text-3xl font-black text-white">94.2%</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-1 text-[10px] font-bold text-white bg-blue-500/50 w-fit px-2 py-0.5 rounded-full border border-blue-400">
              <Zap className="h-3 w-3" /> Sistema Óptimo
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Live Feed & Risk Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Feed */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-black text-slate-800 flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-600" />
              Feed de Casos en Vivo
            </h3>
            <div className="flex items-center gap-2">
               <div className="relative">
                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                 <Input className="h-9 w-64 pl-10 rounded-xl border-slate-200 bg-white shadow-sm text-xs font-medium" placeholder="Buscar por ID o Sector..." />
               </div>
               <Button variant="ghost" size="icon" className="rounded-xl border border-slate-200 h-9 w-9">
                 <Filter className="h-4 w-4 text-slate-500" />
               </Button>
            </div>
          </div>

          <div className="space-y-4">
             {cases.length > 0 ? (
               cases.map((caseItem) => (
                <div key={caseItem.id} className="group bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-2xl hover:shadow-blue-500/5 transition-all relative overflow-hidden">
                  {/* Status Indicator */}
                  <div className={`absolute top-0 left-0 w-1 h-full ${caseItem.status === 'active' ? 'bg-blue-600 animate-pulse' : 'bg-emerald-500'}`} />
                  
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 text-slate-400 group-hover:text-blue-600 group-hover:bg-blue-50 transition-colors">
                        <ArrowUpRight className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">ID: {caseItem.id.slice(0, 8)}</span>
                          <Badge variant="outline" className={cn("text-[10px] font-bold uppercase py-0", getRiskColor(caseItem.risk))}>
                            {caseItem.risk}
                          </Badge>
                        </div>
                        <h4 className="text-base font-bold text-slate-800 uppercase tracking-tight mb-1">{caseItem.agent}</h4>
                        <div className="flex items-center gap-3 text-xs text-slate-500 font-medium">
                          <span className="flex items-center gap-1"><Scale className="h-3 w-3" /> Sector: {caseItem.sector}</span>
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Iniciado: {format(new Date(caseItem.timestamp), "HH:mm", { locale: es })}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-8">
                       <SLACountdown deadline={caseItem.deadline} status={caseItem.status} />
                       <Button variant="ghost" size="icon" className="rounded-xl text-slate-400 hover:text-blue-600">
                         <MoreVertical className="h-5 w-5" />
                       </Button>
                    </div>
                  </div>
                </div>
               ))
             ) : (
               <div className="py-20 text-center bg-white border border-dashed border-slate-200 rounded-[2.5rem]">
                  <Activity className="h-12 w-12 text-slate-200 mx-auto mb-4" />
                  <p className="text-slate-400 font-bold">No hay ingestiones activas en este momento.</p>
               </div>
             )}
          </div>
        </div>

        {/* Sidebar Insights */}
        <div className="space-y-6">
          <h3 className="text-xl font-black text-slate-800 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-sky-600" />
            ROI Legal OS
          </h3>
          
          <Card className="rounded-[2rem] border-slate-200 shadow-sm">
            <CardHeader className="pb-0">
              <CardTitle className="text-sm font-black text-slate-700 uppercase tracking-wider">Ahorro por Sector</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
               <div className="space-y-6">
                 {stats?.sector_dist ? Object.entries(stats.sector_dist).map(([sector, count]: [string, any]) => (
                    <div key={sector}>
                      <div className="flex justify-between text-xs font-bold mb-2">
                        <span className="text-slate-600">{sector}</span>
                        <span className="text-blue-600">{count} casos</span>
                      </div>
                      <div className="h-2 w-full bg-slate-50 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full" 
                          style={{ width: `${(Number(count) / (stats?.total_cases || 1)) * 100}%` }} 
                        />
                      </div>
                    </div>
                 )) : <p className="text-xs text-slate-400">Sin datos de sectores.</p>}
               </div>
            </CardContent>
          </Card>

          <Card className="rounded-[2rem] border-rose-100 bg-rose-50/20 shadow-sm border-dashed">
            <CardHeader className="pb-2">
               <CardTitle className="text-rose-600 flex items-center gap-2 text-sm font-black italic">
                 <ShieldAlert className="h-4 w-4" /> MODALIDAD CRÍTICA
               </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-rose-700 leading-relaxed font-medium">
                Se han detectado <strong>{stats?.risk_dist?.Crítico || 0}</strong> casos de alto riesgo en la última hora que requieren intervención humana del equipo senior.
              </p>
              <Button className="w-full mt-4 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-black text-xs h-10 gap-2">
                Revisión Urgente <ChevronRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
